import type { MatchDetailQuery } from "@/graphql/generated/operations";
import {
  DEFAULT_TEAM_PROFILE_ID,
  EMPTY_MATCH_DATA,
  POWERS_WEIGHT_DEFAULT,
  POWERS_WEIGHT_MIN,
  densifyElementWeights,
  makeIndividualTeamProfile,
} from "./match-design-rules";
import type { FileEntry, MatchData } from "./match-design-types";
import type { ProjectDocument } from "./design-codec";
import { matchSubject, resolveMatchMode, type UiMatchMode } from "./match-mode";
import {
  parseDesign,
  serializeDesign,
  type AiMode,
  type MatchDesign,
  type Project,
} from "./design-codec";

export type GenesisMatch = NonNullable<MatchDetailQuery["match"]>;

// Genesis exposes `name` and `description` as top-level columns on Match. The
// `design` is a shared JSONB contract. This mapping serialises only fields with
// a typed home in genesis `MatchDesign.java`, so other clients can preserve and
// understand every value written by Spark.
//
// MatchData carries additional fields that are workshop-mock UI state
// (matchAccess, images, owner, timeframe, phase, kpis, agents, done,
// requirements.summary/details/etc.) – they ride in React state during a
// session but are NOT persisted. On reload they reset to EMPTY_MATCH_DATA
// defaults. If any of these become product-meaningful, they get a typed
// MatchDesign field on the genesis side first, then a route through here.

export function matchToData(match: GenesisMatch): MatchData {
  const design = parseDesign(match.design);
  const project: Project | null = design.project;

  return {
    ...EMPTY_MATCH_DATA,
    matchTitle: match.name ?? "",
    matchDescription: match.description ?? "",
    teamSize: design.teamSize ?? EMPTY_MATCH_DATA.teamSize,
    teamProfile:
      design.teamProfile && design.teamProfile.id !== DEFAULT_TEAM_PROFILE_ID
        ? {
            id: design.teamProfile.id,
            name: design.teamProfile.name,
            // Preset path: missing keys mean "preset author set this to 0".
            // Older saves persisted sparse JSONB by dropping zeros; densify
            // here so consumers can rely on the map being complete.
            elementWeights: densifyElementWeights(
              design.teamProfile["elementWeights"],
              POWERS_WEIGHT_MIN,
            ),
          }
        : makeIndividualTeamProfile(
            densifyElementWeights(
              design.teamProfile?.["elementWeights"],
              POWERS_WEIGHT_DEFAULT,
            ),
          ),
    recruitingTeamFit: design.recruitingTeamFit ?? null,
    recruitingTeamFitSetup: design.recruitingTeamFitSetup ?? null,
    promptHints: design.aiCopilotText ?? "",
    candidateAssessmentPrompt: design.candidateAssessmentPrompt ?? null,
    candidateAssessmentDetail:
      design.candidateAssessmentDetail ?? EMPTY_MATCH_DATA.candidateAssessmentDetail,
    candidateAssessmentStrictness:
      design.candidateAssessmentStrictness ?? EMPTY_MATCH_DATA.candidateAssessmentStrictness,
    candidateAssessmentOutcomeFocus:
      design.candidateAssessmentOutcomeFocus ?? EMPTY_MATCH_DATA.candidateAssessmentOutcomeFocus,
    requirementLeniency: design.requirementLeniency ?? EMPTY_MATCH_DATA.requirementLeniency,
    powerScoreLens: design.powerScoreLens ?? EMPTY_MATCH_DATA.powerScoreLens,
    missingPowerScore: design.missingPowerScore ?? EMPTY_MATCH_DATA.missingPowerScore,
    conditions: design.conditions,
    unknownConditions: design.unknownConditions,
    companyConditions: design.companyConditions,
    unknownCompanyConditions: design.unknownCompanyConditions,
    aiEnabled: deriveAiEnabled(design, resolveMatchMode(match.matchMode)),
    agenticAi: design.agenticAiSupported ?? EMPTY_MATCH_DATA.agenticAi,
    agents: design.agents,
    // @adr SPARK-0029-match-three-case-model-optimize-assess-rank@4 §2: matchMode is an immutable Match column, not design-blob state.
    // V0035 backfilled the column and stripped the blob, so the entity is the
    // single source of truth; the blob no longer carries it.
    matchMode: match.matchMode ?? null,
    projectName: project?.name ?? "",
    projectShortDescription: project?.["shortDescription"] ?? "",
    projectDescription: project?.description ?? "",
    projectFiles: projectDocumentsToFileEntries(project?.documents),
  };
}

export function dataToDesign(data: MatchData): Record<string, unknown> {
  // Drop half-finished rows whose id-bearing reference hasn't been picked yet.
  // Genesis's MatchDesignDeserializer calls UUID.fromString on these and throws
  // on "", which would abort the whole save. Spark is the only side that knows
  // a row is still being edited, so the filter belongs here.
  const completeConditions = data.conditions.filter((c) => {
    const id = (c as { id?: unknown }).id;
    if (typeof id === "string" && id === "") return false;
    // Gleiche Falle bei Branche: leere industryId crasht genesis beim Solve
    // (NPE in industryExperienceYears) — halbfertige Zeilen nie persistieren.
    const industryId = (c as { industryId?: unknown }).industryId;
    return !(typeof industryId === "string" && industryId === "");
  });

  // Re-apply aiMode at write time so the global toggle is the single source
  // of truth for condition AI behaviour (@adr SPARK-0007-match-requirements-mirror-genesis-design@1), across all modes (spark #233).
  const aiMode: AiMode = data.aiEnabled ? "FULL_AI" : "NO_AI";
  const writtenConditions = completeConditions.map((c) =>
    AI_TYPES.has(c.__typename) ? { ...c, aiMode } : c,
  );

  // Same half-finished-row guard on the company arm, for the same reason: genesis reads
  // CompanyIndustryCondition.industryId as a UUID, so an unpicked Branche row ("") aborts the whole
  // save. null stays null — that distinction is the design's own (see the codec), so a person match
  // never grows an empty company arm just by being saved.
  //
  // A BLANK FACHWISSEN WORDING IS DELIBERATELY *NOT* DROPPED HERE, and that is the one place the two
  // half-finished rows behave differently. Dropping the Branche row is forced: "" is read as a UUID
  // and the alternative is not a bad save but NO save. Nothing forces it for `expertise`, which is a
  // plain string genesis stores and reads back unchanged — so discarding the row would only destroy
  // what the user just authored (the row itself, its Muss/Kann, its Mindeststufe) and do it silently,
  // on an autosave they never asked for. The blank wording is stopped where it does harm instead:
  // incompleteCompanyConditionCount counts it, and the ranking step refuses to run while it does, so
  // the model is never asked whether a company can do "". Same class of problem, same counter, one
  // unavoidable difference in what save may throw away.
  const writtenCompanyConditions =
    data.companyConditions == null
      ? null
      : data.companyConditions
          .filter((c) => !(c.__typename === "CompanyIndustryCondition" && c.industryId === ""))
          // BEIDE ARME FOLGEN `aiEnabled`, der Unternehmens-Arm seit spark #294 wieder. Zwischen dem
          // 09.09. und #294 las er `agenticAi`, weil mit seiner "Bewertungsstil"-Box auch der
          // Schalter verschwunden war und keiner uebrig blieb. Das sind aber zwei Fragen: ob die
          // BEWERTUNG ein Modell befragt (hier), und ob das PROJEKT mit KI-Agenten arbeitet
          // (`agenticAi`, dessen eigener Text genau das sagt). Wer ohne Agenten arbeitete, bekam
          // deshalb auf jedem Fachkompetenz-Kriterium "nicht bewertbar": genesis fragt das Modell nur
          // bei FULL_AI (CompanyMatchConditionEvaluator#needsJudgement), und der Wortlaut-Vergleich
          // davor trifft auf Freitext fast nie.
          .map((c) => (COMPANY_AI_TYPES.has(c.__typename) ? { ...c, aiMode } : c));

  const design: MatchDesign = {
    recruitingTeamFit: data.recruitingTeamFit ?? null,
    recruitingTeamFitSetup: data.recruitingTeamFitSetup ?? null,
    teamCount: 1,
    teamSize: data.teamSize ?? null,
    teamProfile: (() => {
      const tp = data.teamProfile ?? makeIndividualTeamProfile();
      // Densify on write so genesis stores the full snapshot end-to-end.
      // Default differs per scenario: 0 for preset profiles (author intent),
      // 5 for the Individuell synthetic. After this point the JSONB always
      // carries every element key, so reloads can't reintroduce desync.
      const isPreset = tp.id !== DEFAULT_TEAM_PROFILE_ID;
      const missingDefault = isPreset ? POWERS_WEIGHT_MIN : POWERS_WEIGHT_DEFAULT;
      return {
        id: tp.id,
        name: tp.name,
        "elementWeights": densifyElementWeights(tp.elementWeights, missingDefault),
      };
    })(),
    conditions: writtenConditions,
    // Round-trip conditions the codec couldn't type (never dropped on save).
    unknownConditions: data.unknownConditions ?? [],
    companyConditions: writtenCompanyConditions,
    unknownCompanyConditions: data.unknownCompanyConditions ?? [],
    aiCopilotText: data.promptHints ? data.promptHints : null,
    candidateAssessmentPrompt: data.candidateAssessmentPrompt?.trim()
      ? data.candidateAssessmentPrompt
      : null,
    candidateAssessmentDetail: data.candidateAssessmentDetail,
    candidateAssessmentStrictness: data.candidateAssessmentStrictness,
    candidateAssessmentOutcomeFocus: data.candidateAssessmentOutcomeFocus,
    requirementLeniency: data.requirementLeniency,
    powerScoreLens: data.powerScoreLens,
    missingPowerScore: data.missingPowerScore,
    requirementMode: null,
    agenticAiSupported: data.agenticAi,
    // Der Toggle ist der Hauptschalter: aus heisst keine Agents, damit kein unsichtbarer
    // Projekt-Kontext im Blob zurueckbleibt, den niemand mehr sieht.
    agents: data.agenticAi ? data.agents : [],
    // matchMode is not part of the design blob (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4 §2): it lives on the
    // immutable Match.matchMode column and is set at creation, never on save.
    project: buildProjectWire(data),
  };
  return serializeDesign(design);
}

// Returns null if the user has touched nothing Project-shaped yet, so the
// JSONB column stays clean (no empty {} object). As soon as ANY field is
// populated we emit the snapshot so even partial state round-trips.
//
// `documents` is round-tripped (read from data.projectFiles, written back
// untouched) — NOT originated. The attach/detach mutations are the only
// source of new entries; UpdateMatch must echo the current list because
// genesis writes the JSONB design wholesale (MatchController.updateRecord),
// so omitting `documents` here would delete every attached ProjectDocument on
// the next text/teamSize edit. Entries without a backdropFileId are an
// impossible state today (pending uploads live in ChallengeStep, not in
// MatchData) but the filter stays as a defensive safety net.
function buildProjectWire(data: MatchData): Project | null {
  const preservedDocs: ProjectDocument[] = data.projectFiles
    .filter(
      (f): f is FileEntry & { backdropFileId: string } =>
        typeof f.backdropFileId === "string" && f.backdropFileId.length > 0,
    )
    .map((f) => ({
      "backdropFileId": f.backdropFileId,
      name: f.name,
      size: f.size || null,
      "mimeType": f.type || null,
    }));

  const hasContent =
    data.projectName.trim().length > 0 ||
    data.projectShortDescription.trim().length > 0 ||
    data.projectDescription.trim().length > 0 ||
    preservedDocs.length > 0;
  if (!hasContent) return null;
  return {
    name: data.projectName || null,
    "shortDescription": data.projectShortDescription || null,
    description: data.projectDescription || null,
    documents: preservedDocs,
  };
}

function projectDocumentsToFileEntries(
  docs: Project["documents"] | undefined,
): FileEntry[] {
  if (!docs || docs.length === 0) return [];
  return docs.map((d) => ({
    name: d.name,
    size: d.size ?? 0,
    type: d["mimeType"] ?? "",
    // No client-side url until backdrop integration ships; tile renders as a
    // generic document icon using just the filename.
    url: null,
    backdropFileId: d["backdropFileId"],
  }));
}

// Der KI-Schalter ("KI-Unterstützung") isn't persisted directly; it's derived
// from whatever aiMode the conditions already carry. dataToDesign rewrites the
// aiMode of every governed condition at save time to match the toggle, so the
// conditions are the canonical record.
//
// Alle Modi haben genau EINEN Schalter oben (Bewertungsstil) für alle KI-fähigen Typen –
// dasselbe Set, auf das genesis sein candidate-assessment- und AI-Fallback-Gate stützt.
// Früher hatte Optimize einen engeren Scope (nur Expertise/Experience + separater
// City-Toggle in der DetailsSection); der ist zugunsten des einen Top-Schalters entfallen
// (spark #233).
const AI_TYPES: ReadonlySet<string> = new Set([
  "ExpertiseCondition",
  "ExperienceCondition",
  "EducationCondition",
  "CertificateCondition",
  "CityCondition",
]);

// The company arm's ONE AI-capable criterion (concept v17 (b), 2026-09-09). Its own set rather than
// a sixth entry above, because the two arms are read from different lists — `conditions` and
// `companyConditions` — and a shared set would invite a single `.some()` over the wrong one.
//
// Until v17 the company expertise criterion had no switch at all: "hier ist sie die Regel und kein
// Schalter" (2026-09-02). That is reversed, and because the company side has exactly one AI-capable
// criterion, "per criterion" and "per match" are the same switch — which is why the switch it
// follows is the match's own Agentic AI master switch and not a second one of its own.
const COMPANY_AI_TYPES: ReadonlySet<string> = new Set(["CompanyExpertiseCondition"]);

function deriveAiEnabled(design: MatchDesign, mode: UiMatchMode): boolean {
  const governed = (c: { __typename: string }, types: ReadonlySet<string>) => types.has(c.__typename);

  // BEIDE ARME, weil der Schalter beide schreibt (siehe dataToDesign). Ein Design traegt nur den
  // Arm, den sein Modus nutzt, also entscheidet in der Praxis immer genau eine der beiden Listen;
  // ein Design mit beiden Armen gilt als "KI an", sobald eine Seite sie anfordert, und der naechste
  // Speichervorgang vereinheitlicht ohnehin auf den Schalter.
  //
  // Waere hier nur der Personen-Arm gelesen, kaeme der Schalter eines gespeicherten Unternehmens-Matches
  // beim Oeffnen immer auf "aus" zurueck, egal was beim Speichern galt (spark #294).
  const aiCapable = [
    ...design.conditions.filter((c) => governed(c, AI_TYPES)),
    ...(design.companyConditions ?? []).filter((c) => governed(c, COMPANY_AI_TYPES)),
  ];

  // KEIN EINZIGES KI-FAEHIGES KRITERIUM, also nichts abzuleiten — ein frischer Entwurf. Dann
  // entscheidet der Modus, und die beiden Arme antworten verschieden, weil ihre Fachkompetenzen
  // verschieden gespeichert sind: personenseitig sind es Eintraege mit Id, die auch ohne Modell
  // vergleichbar sind, firmenseitig ist es Freitext, den der Wortlaut-Vergleich fast nie trifft.
  // Ein Unternehmens-Match ohne KI liefert deshalb auf jedem Fachkompetenz-Kriterium "nicht bewertbar", und
  // ein Schalter, der aus startet, waere eine Voreinstellung, die nur falsche Ergebnisse erzeugt.
  //
  // Wichtig ist die Reihenfolge: das gilt NUR bei leerer Liste. Sobald ein Kriterium existiert,
  // traegt es die Antwort, auch wenn sie "aus" lautet — sonst koennte man die KI in einem
  // Unternehmens-Match nie ausschalten.
  if (aiCapable.length === 0) return matchSubject(mode) === "companies";

  return aiCapable.some((c) => (c as { aiMode?: AiMode }).aiMode === "FULL_AI");
}

// 3-way rebase for reconciling an external write (the Assistant editing the design) while the user
// has unsaved local edits open in the editor. `baseline` is the server state at our last save,
// `local` is the editor's current overlay, `server` is the freshly-refetched truth (which now
// includes the external write). Field-by-field: a key the user changed since `baseline` keeps its
// local value (their edit wins); every other key adopts `server` (so the agent's change shows
// through). updateMatch(design) is a whole-blob replace and can't merge, so we merge here before the
// next autosave — otherwise the stale overlay clobbers the agent's field-scoped write (genesis#167).
export function rebaseMatchData(
  baseline: MatchData,
  local: MatchData,
  server: MatchData,
): MatchData {
  const out = { ...server } as Record<string, unknown>;
  const b = baseline as unknown as Record<string, unknown>;
  const l = local as unknown as Record<string, unknown>;
  for (const key of Object.keys(l)) {
    if (JSON.stringify(l[key]) !== JSON.stringify(b[key])) out[key] = l[key];
  }
  return out as unknown as MatchData;
}

// True when two MatchData carry the same PERSISTED content (name/description + design). After a
// rebase, decides whether the overlay still differs from server truth (keep it as `pending`) or has
// fully converged (drop it so serverData shows through).
export function matchDataPersistedEqual(a: MatchData, b: MatchData): boolean {
  return (
    (a.matchTitle || null) === (b.matchTitle || null) &&
    (a.matchDescription || null) === (b.matchDescription || null) &&
    JSON.stringify(dataToDesign(a)) === JSON.stringify(dataToDesign(b))
  );
}
